import { api } from './baseApi';

export interface OutletCash {
  id: string;
  currentBalance: string;
  lastUpdatedBy: string;
  createdAt: string;
  updatedAt: string;
  lastUpdatedByUser: {
    name: string;
    username: string;
  };
  transactions: OutletCashTransaction[];
}

export interface OutletCashTransaction {
  id: string;
  outletCashId: string;
  userId: string;
  type: 'INITIAL_DEPOSIT' | 'EXPENSE' | 'DEPOSIT' | 'TRANSFER_TO_DRAWER' | 'TRANSFER_FROM_DRAWER' | 'ADJUSTMENT' | 'SALE';
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  reference?: string;
  referenceType?: 'CASH_DRAWER' | 'EXPENSE' | 'MANUAL' | 'TRANSACTION';
  description: string;
  createdAt: string;
  user: {
    name: string;
    username: string;
  };
}

export interface OutletCashStatus {
  outletCash: OutletCash | null;
  todayActivity: {
    deposits: number;
    expenses: number;
    transfersToDrawers: number;
    transfersFromDrawers: number;
    adjustmentsIn: number;
    adjustmentsOut: number;
  };
  activeCashDrawers: Array<{
    id: string;
    userId: string;
    user: {
      name: string;
      username: string;
    };
    currentBalance: string;
    openedAt: string;
    transferFromOutlet?: string;
    outletDrawer?: {
      name: string;
    };
  }>;
  summary: {
    totalOutletCash: number;
    totalInDrawers: number;
    totalCashInSystem: number;
  };
}

export interface InitializeOutletCashRequest {
  outletId: string;
  initialAmount: number;
  description: string;
}

export interface AdjustOutletCashRequest {
  outletId: string;
  amount: number;
  description: string;
  type: 'DEPOSIT' | 'EXPENSE' | 'ADJUSTMENT';
}

export interface TransferToCashDrawerRequest {
  outletId: string;
  amount: number;
  drawerId: string;
  description?: string;
}

export interface TransferFromCashDrawerRequest {
  outletId: string;
  drawerId: string;
  amount: number;
  description?: string;
}

export interface OutletCashAnalytics {
  currentBalance: number;
  periodAnalytics: Record<string, { count: number; total: number }>;
  dailyBreakdown: Array<{
    date: string;
    deposits: number;
    expenses: number;
    transfersOut: number;
    transfersIn: number;
    adjustments: number;
  }>;
  activeCashDrawers: Array<{
    id: string;
    currentBalance: string;
    transferFromOutlet?: string;
    user: {
      name: string;
      username: string;
    };
    outletDrawer?: {
      name: string;
    };
  }>;
  summary: {
    totalDeposits: number;
    totalExpenses: number;
    totalTransfersOut: number;
    totalTransfersIn: number;
    totalAdjustments: number;
    netCashFlow: number;
  };
}

export interface OutletCashHistoryParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export interface OutletCashHistoryResponse {
  transactions: OutletCashTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const outletCashApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get outlet cash status
    getOutletCashStatus: builder.query<{ success: boolean; data: OutletCashStatus; message?: string }, { outletId: string } | void>({
      query: (arg) => {
        const outletId = typeof arg === 'object' ? arg.outletId : undefined;
        return {
          url: '/outlet-cash/status',
          params: outletId ? { outletId } : undefined,
        };
      },
      providesTags: ['OutletCash'],
    }),

    // Initialize outlet cash
    initializeOutletCash: builder.mutation<
      { success: boolean; data: OutletCash; message: string },
      InitializeOutletCashRequest
    >({
      query: (data) => ({
        url: '/outlet-cash/initialize',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['OutletCash'],
    }),

    // Adjust outlet cash balance
    adjustOutletCash: builder.mutation<
      { success: boolean; data: OutletCash; message: string },
      AdjustOutletCashRequest
    >({
      query: (data) => ({
        url: '/outlet-cash/adjust',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['OutletCash', 'OutletCashTransaction'],
    }),

    // Transfer cash to POS drawer
    transferToCashDrawer: builder.mutation<
      {
        success: boolean;
        data: {
          outletCash: OutletCash;
          cashDrawer: any;
          transferAmount: number;
        };
        message: string
      },
      TransferToCashDrawerRequest
    >({
      query: (data) => ({
        url: '/outlet-cash/transfer-to-drawer',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['OutletCash', 'OutletCashTransaction', 'OutletDrawer'],
    }),

    // Transfer cash from drawer back to outlet
    transferFromCashDrawer: builder.mutation<
      {
        success: boolean;
        data: {
          outletCash: OutletCash;
          cashDrawer: any;
          transferAmount: number;
        };
        message: string
      },
      TransferFromCashDrawerRequest
    >({
      query: (data) => ({
        url: '/outlet-cash/transfer-from-drawer',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['OutletCash', 'OutletCashTransaction', 'OutletDrawer'],
    }),

    // Get outlet cash transaction history
    getOutletCashHistory: builder.query<
      { success: boolean; data: OutletCashHistoryResponse },
      OutletCashHistoryParams & { outletId?: string }
    >({
      query: (params) => ({
        url: '/outlet-cash/transactions',
        params,
      }),
      providesTags: ['OutletCashTransaction'],
    }),

    // Export outlet cash transaction history as CSV
    exportOutletCashHistory: builder.mutation<Blob, OutletCashHistoryParams & { outletId?: string }>({
      query: (params) => ({
        url: '/outlet-cash/transactions/export',
        params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Get outlet cash analytics
    getOutletCashAnalytics: builder.query<
      { success: boolean; data: OutletCashAnalytics },
      { period?: '7d' | '30d' | '90d'; outletId?: string }
    >({
      query: (params) => ({
        url: '/outlet-cash/analytics',
        params,
      }),
      providesTags: ['OutletCashAnalytics'],
    }),
  }),
});

export const {
  useGetOutletCashStatusQuery,
  useInitializeOutletCashMutation,
  useAdjustOutletCashMutation,
  useTransferToCashDrawerMutation,
  useTransferFromCashDrawerMutation,
  useGetOutletCashHistoryQuery,
  useExportOutletCashHistoryMutation,
  useGetOutletCashAnalyticsQuery,
} = outletCashApi;
